import { useTranslations } from '@aikoa/i18n/react'
import { useMemo, useState } from 'react'
import { Pressable, View } from 'react-native'
import Animated, {
  Extrapolation,
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import Icon from '~/components/Icon'
import { Text } from '~/components/ui/text'
import designTokens from '~/lib/designTokens'
import { FrontPageSectionFrame } from './FrontPageSection'
import type { FrontPageRole } from './PublicFrontPage'

type FaqItem = {
  id: string
  question: string
  answer: string
}

function FaqAccordionItem({ item }: { item: FaqItem }) {
  const [isOpen, setIsOpen] = useState(false)
  const progress = useSharedValue(0)

  const handleToggle = () => {
    const next = !isOpen
    setIsOpen(next)
    progress.value = withTiming(next ? 1 : 0, { duration: 250 })
  }

  const contentStyle = useAnimatedStyle(() => ({
    opacity: interpolate(progress.value, [0, 1], [0, 1], Extrapolation.CLAMP),
    maxHeight: interpolate(progress.value, [0, 1], [0, 600], Extrapolation.CLAMP),
  }))

  const chevronStyle = useAnimatedStyle(() => ({
    transform: [
      { rotate: `${interpolate(progress.value, [0, 1], [0, 45], Extrapolation.CLAMP)}deg` },
    ],
  }))

  return (
    <View className="border-b border-secondary/50">
      <Pressable
        onPress={handleToggle}
        className="flex-row items-center justify-between gap-4 py-5 transition-transform duration-150 active:scale-[0.99] active:opacity-90">
        <Text className="flex-1 pr-2 font-semibold text-base leading-snug text-foreground">
          {item.question}
        </Text>

        <Animated.View style={chevronStyle} className="shrink-0">
          <Icon name="add" size={18} color={designTokens.foreground} />
        </Animated.View>
      </Pressable>

      <Animated.View style={contentStyle} className="overflow-hidden">
        <View className="pb-6 pr-2 sm:pr-8">
          <Text className="text-base leading-relaxed text-foreground">{item.answer}</Text>
        </View>
      </Animated.View>
    </View>
  )
}

export function FrontPageFaq({ role }: { role: FrontPageRole }) {
  const { t } = useTranslations('home')

  const faqItems = useMemo<FaqItem[]>(() => {
    if (role === 'realtor') {
      return [
        {
          id: 'whyRequest',
          question: t(($) => $.frontpage.faq.realtor.whyRequestTitle),
          answer: t(($) => $.frontpage.faq.realtor.whyRequestAnswer),
        },
        {
          id: 'whenToRequest',
          question: t(($) => $.frontpage.faq.realtor.whenToRequestTitle),
          answer: t(($) => $.frontpage.faq.realtor.whenToRequestAnswer),
        },
        {
          id: 'howLongOwned',
          question: t(($) => $.frontpage.faq.realtor.howLongOwnedTitle),
          answer: t(($) => $.frontpage.faq.realtor.howLongOwnedAnswer),
        },
        {
          id: 'ensureCommission',
          question: t(($) => $.frontpage.faq.realtor.ensureCommissionTitle),
          answer: t(($) => $.frontpage.faq.realtor.ensureCommissionAnswer),
        },
        {
          id: 'commissionSplit',
          question: t(($) => $.frontpage.faq.realtor.commissionSplitTitle),
          answer: t(($) => $.frontpage.faq.realtor.commissionSplitAnswer),
        },
        {
          id: 'brandRisk',
          question: t(($) => $.frontpage.faq.realtor.brandRiskTitle),
          answer: t(($) => $.frontpage.faq.realtor.brandRiskAnswer),
        },
      ]
    }

    return [
      {
        id: 'whyProfile',
        question: t(($) => $.frontpage.faq.buyer.whyProfileTitle),
        answer: t(($) => $.frontpage.faq.buyer.whyProfileAnswer),
      },
      {
        id: 'whenProfile',
        question: t(($) => $.frontpage.faq.buyer.whenProfileTitle),
        answer: t(($) => $.frontpage.faq.buyer.whenProfileAnswer),
      },
      {
        id: 'howLongSearch',
        question: t(($) => $.frontpage.faq.buyer.howLongSearchTitle),
        answer: t(($) => $.frontpage.faq.buyer.howLongSearchAnswer),
      },
      {
        id: 'beforePublic',
        question: t(($) => $.frontpage.faq.buyer.beforePublicTitle),
        answer: t(($) => $.frontpage.faq.buyer.beforePublicAnswer),
      },
      {
        id: 'spam',
        question: t(($) => $.frontpage.faq.buyer.spamTitle),
        answer: t(($) => $.frontpage.faq.buyer.spamAnswer),
      },
      {
        id: 'contactDetails',
        question: t(($) => $.frontpage.faq.buyer.contactDetailsTitle),
        answer: t(($) => $.frontpage.faq.buyer.contactDetailsAnswer),
      },
      {
        id: 'commitment',
        question: t(($) => $.frontpage.faq.buyer.commitmentTitle),
        answer: t(($) => $.frontpage.faq.buyer.commitmentAnswer),
      },
      {
        id: 'editSearch',
        question: t(($) => $.frontpage.faq.buyer.editSearchTitle),
        answer: t(($) => $.frontpage.faq.buyer.editSearchAnswer),
      },
    ]
  }, [role, t])

  return (
    <FrontPageSectionFrame className="rounded-t-content bg-tertiary-foreground pt-16 sm:pt-24">
      <View className="gap-8 sm:gap-10">
        <View className="size-18 aspect-square flex-row items-center justify-center self-center rounded-full bg-white">
          <Icon name="chat-question" size={30} color={designTokens.foreground} />
        </View>
        <Text className="font-semibold text-2xl leading-tight tracking-tight text-foreground xs:text-3xl">
          {t(($) => $.frontpage.faq.title)}
        </Text>

        <View>
          {faqItems.map((item) => (
            <FaqAccordionItem key={item.id} item={item} />
          ))}
        </View>
      </View>
    </FrontPageSectionFrame>
  )
}
